/**
 * Area Utilities
 * 
 * Functions for handling geographic area codes, slugs, and mappings
 * Supports states (2-digit FIPS codes) and MSAs (5-digit codes)
 */

/**
 * Generate URL-friendly slug from area name
 */
export function generateAreaSlug(areaName: string): string {
  return areaName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Generate MSA slug from area code and name
 * Example: 35620, "New York-Newark-Jersey City" -> "new-york-newark-jersey-city"
 */
export function generateMSASlug(areaCode: string, areaName: string): string {
  // Use area name if available, otherwise use area code
  if (areaName) {
    return generateAreaSlug(areaName);
  }
  // Fallback to area code if no name
  return `msa-${areaCode}`;
}

/**
 * Check if an area code is an MSA (5 digits) or state (2 digits)
 */
export function isMSACode(areaCode: string): boolean {
  // MSAs are 5-digit codes, states are 2-digit FIPS codes
  return /^\d{5}$/.test(areaCode);
}

/**
 * Check if an area code is a state (2 digits)
 */
export function isStateCode(areaCode: string): boolean {
  // States are 2-digit FIPS codes (01-56, excluding some gaps)
  return /^\d{2}$/.test(areaCode) && areaCode !== '99'; // 99 is national
}

/**
 * Check if an area code is national (99)
 */
export function isNationalCode(areaCode: string): boolean {
  return areaCode === '99';
}

/**
 * Parse area code from URL slug
 * Handles both state slugs and MSA slugs
 */
export function parseAreaCodeFromSlug(slug: string, areaMap?: Map<string, string>): string | null {
  // If we have an area map, use it
  if (areaMap) {
    return areaMap.get(slug) || null;
  }
  
  // Try to match state abbreviations
  const stateSlugMap: Record<string, string> = {
    'alabama': '01', 'alaska': '02', 'arizona': '04', 'arkansas': '05',
    'california': '06', 'colorado': '08', 'connecticut': '09', 'delaware': '10',
    'florida': '12', 'georgia': '13', 'hawaii': '15', 'idaho': '16',
    'illinois': '17', 'indiana': '18', 'iowa': '19', 'kansas': '20',
    'kentucky': '21', 'louisiana': '22', 'maine': '23', 'maryland': '24',
    'massachusetts': '25', 'michigan': '26', 'minnesota': '27', 'mississippi': '28',
    'missouri': '29', 'montana': '30', 'nebraska': '31', 'nevada': '32',
    'new-hampshire': '33', 'new-jersey': '34', 'new-mexico': '35', 'new-york': '36',
    'north-carolina': '37', 'north-dakota': '38', 'ohio': '39', 'oklahoma': '40',
    'oregon': '41', 'pennsylvania': '42', 'rhode-island': '44', 'south-carolina': '45',
    'south-dakota': '46', 'tennessee': '47', 'texas': '48', 'utah': '49',
    'vermont': '50', 'virginia': '51', 'washington': '53', 'west-virginia': '54',
    'wisconsin': '55', 'wyoming': '56', 'district-of-columbia': '11',
    'united-states': '99', 'national': '99'
  };
  
  const normalizedSlug = slug.toLowerCase();
  if (stateSlugMap[normalizedSlug]) {
    return stateSlugMap[normalizedSlug];
  }
  
  // Check if it's an MSA slug (starts with msa-)
  if (normalizedSlug.startsWith('msa-')) {
    const msaCode = normalizedSlug.replace('msa-', '');
    if (/^\d{5}$/.test(msaCode)) {
      return msaCode;
    }
  }
  
  return null;
}

/**
 * Format area name for display
 */
export function formatAreaName(areaName: string): string {
  // Capitalize first letter of each word
  return areaName
    .split(/[\s-]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Get area type from area code
 */
export function getAreaType(areaCode: string): 'national' | 'state' | 'msa' | 'unknown' {
  if (isNationalCode(areaCode)) {
    return 'national';
  }
  if (isStateCode(areaCode)) {
    return 'state';
  }
  if (isMSACode(areaCode)) {
    return 'msa';
  }
  return 'unknown';
}

